import { useState, FormEvent } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    topic: '',
    message: '',
    requestCall: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('topic', formData.topic);
      formBody.append('message', formData.message);
      formBody.append('requestCall', formData.requestCall ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d67ln2vmvg9ih2c7980g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          topic: '',
          message: '',
          requestCall: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      label: 'Email',
      value: 'summit@womeninwealth.com',
      link: 'mailto:summit@womeninwealth.com',
    },
    {
      icon: 'ri-map-pin-line',
      label: 'Location',
      value: 'Beirut, Lebanon',
      link: null,
    },
    {
      icon: 'ri-calendar-line',
      label: 'Event Date',
      value: 'March 2026',
      link: null,
    },
  ];

  const socialLinks = [
    { icon: 'ri-linkedin-fill', label: 'LinkedIn', url: 'https://linkedin.com' },
    { icon: 'ri-instagram-line', label: 'Instagram', url: 'https://instagram.com' },
    { icon: 'ri-twitter-x-line', label: 'Twitter', url: 'https://twitter.com' },
  ];
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Women In Wealth Summit",
    "description": "Get in touch with the Women In Wealth Summit concierge team for participation, partnerships, media inquiries, or bespoke arrangements.",
    "mainEntity": {
      "@type": "Organization",
      "name": "Women In Wealth Summit",
      "email": "summit@womeninwealth.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Beirut",
        "addressCountry": "LB"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Us | Women In Wealth Summit · Beirut 2026"
        description="Contact the Women In Wealth Summit concierge team. For participation inquiries, partnership opportunities, media requests, or bespoke arrangements. Email: summit@womeninwealth.com"
        keywords="contact women in wealth, summit contact, concierge team, partnership inquiries, media contact, Beirut summit contact"
        canonical={`${siteUrl}/contact`}
        schema={schema}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-champagne-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-blush-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative px-8 lg:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
              <i className="ri-customer-service-2-line text-champagne-400"></i>
              <span className="text-sm text-white/90 font-medium">Summit Concierge</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight">
              Get in Touch
            </h1>
            <p className="text-xl text-white/70 leading-relaxed max-w-2xl mx-auto mb-4">
              For participation, partnerships, media, or bespoke arrangements.
            </p>
            <p className="text-base text-white/60 leading-relaxed">
              The concierge team works closely with the WealthRise Management team to support every attendee.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="px-8 lg:px-16">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left - Info */}
            <div className="space-y-12">
              <div>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
                  Let's Connect
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Whether you're interested in attending, partnering with us, or have a specific inquiry, we're here to help.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="group p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 hover:border-champagne-300 hover:shadow-lg hover:shadow-champagne-100/50 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-champagne-100 rounded-xl text-champagne-600 group-hover:bg-champagne-600 group-hover:text-white transition-all duration-300">
                        <i className={`${info.icon} text-xl`}></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                          {info.label}
                        </p>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-gray-900 font-medium hover:text-champagne-600 transition-colors"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-gray-900 font-medium">{info.value}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="pt-8 border-t border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-6">
                  Follow the Summit
                </p>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-xl border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:border-champagne-600 hover:text-champagne-600 hover:bg-champagne-50 transition-all duration-300 cursor-pointer"
                      aria-label={social.label}
                    >
                      <i className={`${social.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* Trust Badge */}
              <div className="p-6 bg-gradient-to-br from-navy-900 to-navy-800 rounded-2xl text-white">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg">
                    <i className="ri-shield-check-line text-xl text-champagne-400"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Discretion Guaranteed</h4>
                    <p className="text-sm text-white/70 leading-relaxed">
                      All inquiries are handled with the utmost confidentiality and care.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div>
              <form onSubmit={handleSubmit} data-readdy-form id="contact_form" className="space-y-6">
                {/* Name Field */}
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="peer w-full bg-white border-2 border-gray-200 focus:border-champagne-600 outline-none py-4 px-5 text-gray-900 transition-all rounded-xl pt-6"
                    placeholder=" "
                  />
                  <label
                    className={`absolute left-5 transition-all duration-200 pointer-events-none ${
                      formData.name || focusedField === 'name'
                        ? 'top-2 text-xs text-champagne-600 font-semibold'
                        : 'top-4 text-base text-gray-500'
                    }`}
                  >
                    Full Name *
                  </label>
                </div>

                {/* Email Field */}
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="peer w-full bg-white border-2 border-gray-200 focus:border-champagne-600 outline-none py-4 px-5 text-gray-900 transition-all rounded-xl pt-6"
                    placeholder=" "
                  />
                  <label
                    className={`absolute left-5 transition-all duration-200 pointer-events-none ${
                      formData.email || focusedField === 'email'
                        ? 'top-2 text-xs text-champagne-600 font-semibold'
                        : 'top-4 text-base text-gray-500'
                    }`}
                  >
                    Email Address *
                  </label>
                </div>

                {/* Topic Field */}
                <div className="relative">
                  <select
                    name="topic"
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    onFocus={() => setFocusedField('topic')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="w-full bg-white border-2 border-gray-200 focus:border-champagne-600 outline-none py-4 px-5 text-gray-900 transition-all rounded-xl cursor-pointer pt-6 appearance-none"
                  >
                    <option value="">Select a topic</option>
                    <option value="Participation">Participation</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Media">Media</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                  <label
                    className={`absolute left-5 transition-all duration-200 pointer-events-none ${
                      formData.topic || focusedField === 'topic'
                        ? 'top-2 text-xs text-champagne-600 font-semibold'
                        : 'top-4 text-base text-gray-500'
                    }`}
                  >
                    Topic *
                  </label>
                  <i className="ri-arrow-down-s-line absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>

                {/* Message Field */}
                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    required
                    maxLength={500}
                    rows={5}
                    className="peer w-full bg-white border-2 border-gray-200 focus:border-champagne-600 outline-none p-5 text-gray-900 transition-all rounded-xl resize-none pt-6"
                    placeholder=" "
                  ></textarea>
                  <label
                    className={`absolute left-5 transition-all duration-200 pointer-events-none ${
                      formData.message || focusedField === 'message'
                        ? 'top-2 text-xs text-champagne-600 font-semibold'
                        : 'top-5 text-base text-gray-500'
                    }`}
                  >
                    Your Message *
                  </label>
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {/* Checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="requestCall"
                    id="requestCall"
                    checked={formData.requestCall}
                    onChange={(e) => setFormData({ ...formData, requestCall: e.target.checked })}
                    className="w-5 h-5 mt-0.5 rounded border-2 border-gray-300 text-champagne-600 focus:ring-champagne-600 cursor-pointer"
                  />
                  <label htmlFor="requestCall" className="text-sm text-gray-700 cursor-pointer">
                    Request a private call with the concierge team
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-navy-900 text-white rounded-xl font-medium hover:bg-navy-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-navy-900/20 hover:shadow-xl hover:shadow-navy-900/30 whitespace-nowrap cursor-pointer flex items-center justify-center gap-3"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line text-lg animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-fill text-lg"></i>
                    </>
                  )}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="p-5 bg-green-50 border-2 border-green-200 rounded-xl animate-fadeIn">
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-green-900 mb-1">Message Sent Successfully</p>
                        <p className="text-sm text-green-700">
                          Thank you for reaching out. Our concierge team will be in touch within 24 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-5 bg-red-50 border-2 border-red-200 rounded-xl animate-fadeIn">
                    <div className="flex items-start gap-3">
                      <i className="ri-error-warning-fill text-xl text-red-600 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-red-900 mb-1">Error Sending Message</p>
                        <p className="text-sm text-red-700">
                          There was an error sending your message. Please try again or email us directly.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
